// ABOUTME: The Jobs page while PRO_LISTING_FEED is on: pro-core's sections in its order, each drawn by its kind: the job
// ABOUTME: once hired, the application process as the applications block, the listings as the job feed with its filters.
import { ReactNode, useRef } from 'react';
import { useQueryClient } from 'react-query';
import Box from '@mui/material/Box';
import { NotFoundErrorScreen } from 'components/screens';
import { ApplicationsSkeleton } from 'modules/applications/components/ApplicationsSkeleton';
import { getApiLocale, getTrans } from 'modules/i18n';
import {
  INITIAL_FEED_CRITERIA,
  JobFeed,
  JobFeedSkeleton,
  USE_JOB_LISTINGS_QUERY_KEY,
  useApplyV2Enabled
} from 'modules/jobListings';
import { useAccessToken } from 'utils/auth';
import { useFeed } from '../hooks';
import { FeedSection, ListingsSection } from '../types';
import { firstListingsPage } from '../utils';
import { ApplicationProcess } from './ApplicationProcess';
import { EmploymentCard } from './EmploymentCard';

/**
 * Hands the listings section's first page to the job feed's own query, so the feed below starts from it instead of
 * asking for the same page again. Only for the criteria a fresh visit reads with, and only when nothing is cached yet.
 * It runs while this component renders, once per section: the job feed below starts its query in its own effect,
 * which runs before any effect of this parent would.
 */
const SEEDED_FRESH_MS = 60_000;

const useSeedListings = (section: ListingsSection | undefined) => {
  const queryClient = useQueryClient();
  const { token } = useAccessToken();
  const language = getApiLocale();
  const seeded = useRef<ListingsSection | undefined>(undefined);

  if (section && token && seeded.current !== section) {
    seeded.current = section;
    const key = [
      USE_JOB_LISTINGS_QUERY_KEY,
      token,
      language,
      JSON.stringify(INITIAL_FEED_CRITERIA)
    ];
    if (!queryClient.getQueryData(key)) {
      // Fresh for a minute, or the job feed's mount would ask for the page just seeded.
      queryClient.setQueryDefaults(key, { staleTime: SEEDED_FRESH_MS });
      queryClient.setQueryData(key, {
        pages: [firstListingsPage(section)],
        pageParams: [0]
      });
    }
  }
};

export const FeedSkeleton = () => (
  <Box data-testid="feed-skeleton">
    <ApplicationsSkeleton />
    <JobFeedSkeleton />
  </Box>
);

export const FeedSections = ({
  sections,
  now = new Date()
}: {
  sections: FeedSection[];
  /** The instant "today" is measured from; injectable so a test does not fake timers. */
  now?: Date;
}) => {
  const listings = sections.find(
    (section): section is ListingsSection => section.kind === 'LISTINGS'
  );
  useSeedListings(listings);
  const { isEnabled: canHide } = useApplyV2Enabled();

  return (
    <Box data-testid="feed-sections">
      {sections.map(section => {
        switch (section.kind) {
          case 'EMPLOYMENT':
            return section.items.map(item => (
              <EmploymentCard key={item.assignmentRefId} item={item} />
            ));
          case 'APPLICATION_PROCESS':
            return (
              <ApplicationProcess
                key={section.key}
                section={section}
                now={now}
                canHide={canHide}
              />
            );
          case 'LISTINGS':
            return <JobFeed key={section.key} />;
          default:
            return null;
        }
      })}
    </Box>
  );
};

export const FeedError = () => (
  <NotFoundErrorScreen
    title={getTrans('feed.error.title')}
    description1={getTrans('jobs.error.body1')}
    description2={getTrans('jobs.error.body2')}
  />
);

/**
 * The Jobs page's content once PRO_LISTING_FEED is on: the skeleton, the feed's sections, or the failure. When pro-core
 * answers that the feed is off for this talent, `fallback` (today's page) is shown instead.
 */
export const JobsFeed = ({ fallback }: { fallback: ReactNode }) => {
  const feed = useFeed();

  if (feed.isUnavailable) return <>{fallback}</>;
  if (feed.isLoading) return <FeedSkeleton />;
  if (feed.isError || !feed.sections) return <FeedError />;
  return <FeedSections sections={feed.sections} />;
};
